import { IconButton } from "@chakra-ui/button";
import { DeleteIcon, RepeatClockIcon } from "@chakra-ui/icons";
import { Box, HStack } from "@chakra-ui/layout";
import { SkeletonText } from "@chakra-ui/skeleton";
import { Table, Tbody, Td, Th, Thead, Tr } from "@chakra-ui/table";
import { Tag } from "@chakra-ui/tag";
import React from "react";
import { memo } from "react";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import DateUtil from "../../util/DateUtil";

interface MembershipRenewalTableProps {
  membershipRenewals:
    | CherryHubAdminApi.MembershipRenewalFormConfigurationSummaryItem[]
    | null;
  isLoading: boolean;
  debouncedQuery: string;
  disabledActions?: boolean;
  onClickEditMembershipRenewal: (
    membershipRenewal: CherryHubAdminApi.MembershipRenewalFormConfigurationSummaryItem
  ) => void;
  onClickDeleteMembershipRenewal: (
    membershipRenewal: CherryHubAdminApi.MembershipRenewalFormConfigurationSummaryItem
  ) => void;
  onClickRestoreMembershipRenewal: (
    membershipRenewal: CherryHubAdminApi.MembershipRenewalFormConfigurationSummaryItem
  ) => void;
}

export const MembershipRenewalTable = memo(
  ({
    membershipRenewals,
    isLoading,
    debouncedQuery,
    disabledActions,
    onClickEditMembershipRenewal,
    onClickDeleteMembershipRenewal,
    onClickRestoreMembershipRenewal,
  }: MembershipRenewalTableProps) => {
    return (
      <Box w="100%" overflowX="auto">
        <Table size="sm">
          <Thead>
            <Tr>
              <Th w="0.1%" whiteSpace="nowrap">
                Membership Renewal #
              </Th>
              <Th>Name</Th>
              <Th>Status</Th>
              <Th>Created</Th>
              <Th>Last Modified</Th>
              <Th>&nbsp;</Th>
            </Tr>
          </Thead>
          <Tbody>
            {!isLoading &&
              (membershipRenewals ?? []).map((row) => (
                <Tr key={row.Meta?.Id}>
                  <Td textAlign="center" w="0.1%" whiteSpace="nowrap">
                    {row.MembershipRenewalFormConfigurationNo}
                  </Td>
                  <Td
                    title="Edit membership renewal"
                    _hover={
                      !row.Meta?.IsDeleted
                        ? { textDecoration: "underline", cursor: "pointer" }
                        : {}
                    }
                    onClick={() =>
                      !row.Meta?.IsDeleted && onClickEditMembershipRenewal(row)
                    }
                  >
                    {!!row.Meta?.IsDeleted && (
                      <Tag size="sm" mr="2">
                        Deleted
                      </Tag>
                    )}
                    {row.DisplayName}
                  </Td>
                  <Td width="0.1%" whiteSpace="nowrap">
                    <Tag
                      size="sm"
                      bgColor={row.IsEnabled ? "" : "cherryButton.500"}
                      color={row.IsEnabled ? "" : "white"}
                    >
                      {row.IsEnabled ? "Enabled" : "Disabled"}
                    </Tag>
                  </Td>
                  <Td width="0.1%" whiteSpace="nowrap">
                    {DateUtil.getLocalDateFormatFromString(
                      row.Meta?.DateCreated
                    )}
                  </Td>
                  <Td width="0.1%" whiteSpace="nowrap">
                    {DateUtil.getLocalDateFormatFromString(
                      row.Meta?.DateUpdated
                    )}
                  </Td>
                  <Td width="0.1%" whiteSpace="nowrap">
                    <HStack spacing={2} justifyContent="center">
                      {(row.Meta?.IsDeleted && (
                        <IconButton
                          aria-label="Restore membership renewal."
                          title="Restore membership renewal."
                          icon={<RepeatClockIcon />}
                          size="xs"
                          isDisabled={disabledActions}
                          onClick={() => onClickRestoreMembershipRenewal(row)}
                        />
                      )) || (
                        <IconButton
                          marginLeft="1"
                          aria-label="Delete membership renewal."
                          title="Delete membership renewal."
                          icon={<DeleteIcon />}
                          size="xs"
                          isDisabled={disabledActions}
                          onClick={() => onClickDeleteMembershipRenewal(row)}
                        />
                      )}
                    </HStack>
                  </Td>
                </Tr>
              ))}
            {membershipRenewals?.length === 0 && !isLoading && (
              <Tr>
                <Td colSpan={5} textAlign="center">
                  No membership renewal form found matching query '
                  {debouncedQuery}'
                </Td>
              </Tr>
            )}
            {isLoading && (
              <Tr>
                <Td width="5%">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="65%">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="5%" whiteSpace="nowrap">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="5%" whiteSpace="nowrap">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="10%" whiteSpace="nowrap">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="10%" whiteSpace="nowrap">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
    );
  }
);
